import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, BookOpen, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import blogPosts from '../data/blogPosts';

import { BreadcrumbSchema, SITE_URL } from '../components/GlobalSchema';
const SITE = SITE_URL;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  // `blogPosts` is a module-level import (frozen at build time);
  // `p`/`set` are closure-local. No reactive deps.
  const categories = useMemo(() => {
    const set = new Set(blogPosts.map((p) => p.category));
    return ['All', ...Array.from(set)];
  }, []);

  // `blogPosts` is module-level; `p`/`a`/`b`/`q` are closure-local.
  // Only `searchTerm`/`activeCategory` are real reactive deps.
  const filteredPosts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return blogPosts
      .filter((p) => (activeCategory === 'All' ? true : p.category === activeCategory))
      .filter((p) =>
        q
          ? p.title.toLowerCase().includes(q) ||
            p.excerpt.toLowerCase().includes(q) ||
            p.category.toLowerCase().includes(q)
          : true
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [searchTerm, activeCategory]);

  const breadcrumbs = [
    { name: 'Home', url: `${SITE}/` },
    { name: 'Blog', url: `${SITE}/blog` }
  ];

  // Blog ItemList JSON-LD for AEO
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'ABL Finance Insights',
    url: `${SITE}/blog`,
    description:
      'Expert analysis and guides on SBLC, Bank Guarantees, Trade Finance, Project Finance, and Corporate Finance from ABL Finance (WBA UK Holdings 1 Limited).',
    publisher: { '@id': `${SITE}/#organization` },
    blogPost: blogPosts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `${SITE}/blog/${p.slug}`,
      datePublished: p.date,
      author: { '@type': 'Organization', name: p.author }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>SBLC, Bank Guarantee & Trade Finance Blog | ABL Finance</title>
        <meta
          name="description"
          content="Expert guides on Standby Letters of Credit, Bank Guarantees, Trade Finance, Project Finance and Corporate Finance from the ABL Finance team."
        />
        <meta
          name="keywords"
          content="SBLC blog, trade finance insights, bank guarantee guide, project finance articles, corporate finance blog, ABL Finance"
        />
        <link rel="canonical" href={`${SITE}/blog`} />
        <meta property="og:title" content="SBLC, Bank Guarantee & Trade Finance Blog | ABL Finance" />
        <meta
          property="og:description"
          content="Expert guides on Standby Letters of Credit, Bank Guarantees, Trade Finance, Project Finance and Corporate Finance from the ABL Finance team."
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ABL Finance" />
        <meta property="og:url" content={`${SITE}/blog`} />
        <meta property="og:image" content={`${SITE_URL}/logo.png`} />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SBLC, Bank Guarantee & Trade Finance Blog | ABL Finance" />
        <meta name="twitter:description" content="Expert guides on Standby Letters of Credit, Bank Guarantees, Trade Finance, Project Finance and Corporate Finance from the ABL Finance team." />
        <meta name="twitter:image" content={`${SITE_URL}/logo.png`} />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <BreadcrumbSchema items={breadcrumbs} />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-r from-slate-900 to-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-emerald-600/30 border border-emerald-500/50 px-4 py-1 rounded-full mb-6">
            <BookOpen className="w-4 h-4 mr-2" />
            <span className="text-sm font-semibold tracking-wide">ABL Finance Insights</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
            data-testid="blog-h1"
          >
            Trade Finance & SBLC Insights
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Practical guides, market analysis and expert commentary on Standby Letters of Credit,
            Bank Guarantees, Trade Finance and Corporate Finance from the ABL Finance team.
          </p>
        </div>
      </section>

      {/* Search + Categories */}
      <section className="py-10 bg-white border-b border-slate-100 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 py-5"
                data-testid="blog-search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="blog-category-filters">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  data-testid={`blog-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
                    activeCategory === cat
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-500 hover:text-emerald-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-slate-600" data-testid="blog-empty-state">
                No articles match your search. Try a different keyword or category.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8 flex items-center justify-between">
                <p className="text-slate-600" data-testid="blog-results-count">
                  Showing {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-posts-grid">
                {filteredPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <Card className="h-full flex flex-col hover:shadow-xl transition-shadow border-slate-200 hover:border-emerald-300">
                      <CardHeader>
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                            {post.category}
                          </span>
                          <span className="inline-flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {post.readTime}
                          </span>
                        </div>
                        <CardTitle className="text-xl leading-snug group-hover:text-emerald-700 transition-colors">
                          {post.title}
                        </CardTitle>
                        <CardDescription className="flex items-center text-xs mt-2">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(post.date)}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex-grow flex flex-col">
                        <p className="text-slate-600 line-clamp-3 mb-4 flex-grow">{post.excerpt}</p>
                        <span className="inline-flex items-center text-emerald-600 font-semibold text-sm group-hover:text-emerald-700">
                          Read article
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Discuss Your Trade Finance Requirements
          </h2>
          <p className="text-lg md:text-xl text-emerald-50 mb-8 leading-relaxed">
            Our team has over 65 years of combined experience facilitating SBLCs, Bank Guarantees
            and structured trade finance across 45+ countries. Speak to a specialist today.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white text-emerald-700 hover:bg-gray-100 px-8 py-6 text-base"
              data-testid="blog-cta-contact"
            >
              Request a Consultation
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <p className="text-sm text-slate-500 text-right">Last updated: May 2026</p>
      </div>
    </div>
  );
};

export default Blog;
